import React, { useState, useEffect, useContext, useCallback } from "react";
import { Search } from "lucide-react";
import LocationDropdowns from "../../components/LocationDropdowns";
import { DocumentServices } from "../../services/DocumentService";
import { RegionService } from "../../services/RegionService";
import { PerfilService } from "../../services/PerfilService";
import { UsuarioService } from "../../services/UsuarioService";
import { ClienteService } from "../../services/ClienteService";
import Preloader from "../../components/Preloader";
import { useNavigate, useParams } from "react-router-dom";
import { PostulanteService } from "../../services/PostulanteService";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/AuthContext";

const initialState = {
    tipoDocumento: "",
    numero: "",
    perfil: "",
    nombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    celular: "",
    email: "",
    fechaNacimiento: "",
    departamento: "",
    provincia: "",
    distrito: "",
    direccion: "",
    id_region: "",
    jefe: "",
    supervisor: "",
    id_postulante: "",
    usuario: ""
};

const perfilesEquipoTrabajo = new Set([
    "Vendedor",
    "Supervisor",
    "Callcenter",
    "Asesor",
]);

function NuevoUsuario() {
    const { token } = useContext(AuthContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [regionesDisponibles, setRegionesDisponibles] = useState([]);
    const [loadingRegiones, setLoadingRegiones] = useState(false);
    const [jefesDisponibles, setJefesDisponibles] = useState([]);
    const [supervisoresDisponibles, setSupervisoresDisponibles] = useState([]);
    const [loadingJefes, setLoadingJefes] = useState(false);
    const [loadingSupervisores, setLoadingSupervisores] = useState(false);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [perfiles, setPerfiles] = useState([]);

    // Estado para las ubicaciones
    const [locationData, setLocationData] = useState({
        department: "",
        province: "",
        district: "",
        zone: "",
    });

    const tipoSeleccionado = perfiles.find((td) => td.id === form.perfil);

    // Función para validar que solo se ingresen números
    const validateNumericInput = (value) => {
        return value.replace(/\D/g, "");
    };

    // Función para manejar cambios en campos numéricos
    const handleNumericChange = (e) => {
        const { name, value } = e.target;
        const numericValue = validateNumericInput(value);
        setForm((prev) => ({
            ...prev,
            [name]: numericValue,
        }));
    };

    // Función para cargar las regiones disponibles
    const cargarRegiones = useCallback(async () => {
        setLoadingRegiones(true);
        try {
            const data = await RegionService.fetchRegiones(token);
            setRegionesDisponibles(data);
        } catch (error) {
            console.error("Error al cargar regiones:", error);
        } finally {
            setLoadingRegiones(false);
        }
    }, [token]);

    const cargarTipoDocumentos = useCallback(async () => {
        setLoadingRegiones(true);
        try {
            const tipos = await DocumentServices.getDocuments(token);
            setTiposDocumentos(tipos || []);
        } catch (error) {
            console.error("Error al cargar tipos de Documentos:", error);
        } finally {
            setLoadingRegiones(false);
        }
    }, [token]);

    const cargarPerfiles = useCallback(async () => {
        setLoadingRegiones(true);
        try {
            const data = await PerfilService.fetchPerfiles(token);
            setPerfiles(data);
        } catch (error) {
            console.error("Error al cargar perfiles:", error);
        } finally {
            setLoadingRegiones(false);
        }
    }, [token]);

    const cargarJefes = useCallback(async () => {
        setLoadingJefes(true);
        try {
            const data = await UsuarioService.fetchJefes(token);
            setJefesDisponibles(data);
        } catch (error) {
            setJefesDisponibles([]);
            console.log(error);
        } finally {
            setLoadingJefes(false);
        }
    }, [token]);

    const handleBackToList = () => {
        navigate("/admin/rrhh");
    };

    const cargarSupervisores = useCallback(async () => {
        setLoadingSupervisores(true);
        try {
            const data = await UsuarioService.fetchSupervisores(token);
            setSupervisoresDisponibles(data);
        } catch (error) {
            setSupervisoresDisponibles([]);
            console.log(error);
        } finally {
            setLoadingSupervisores(false);
        }
    }, [token]);

    const fetchPostulantes = useCallback(async () => {
        setLoading(true);
        try {
            const postulanteData = await PostulanteService.fetchPostulanteById(token, id);

            if (postulanteData?.id_estado_postulante === 5) {
                const safeValue = (value) => value ?? '';
                setForm({
                    ...initialState,
                    nombre: safeValue(postulanteData.nombre),
                    apellidoPaterno: safeValue(postulanteData.apellidoPaterno),
                    apellidoMaterno: safeValue(postulanteData.apellidoMaterno),
                    tipoDocumento: safeValue(postulanteData.id_tipo_documento),
                    numero: safeValue(postulanteData.numero_documento),
                    celular: safeValue(postulanteData.telefono),
                    email: safeValue(postulanteData.email),
                    fechaNacimiento: formatDate(postulanteData.fechaNacimiento),
                    id_postulante: id,
                    departamento: postulanteData?.department_id ?? '',
                    provincia: postulanteData?.province_id ?? '',
                    distrito: postulanteData?.district_id ?? '',
                    direccion: postulanteData?.direccion ?? '',
                });

                setLocationData({
                    department: postulanteData?.department_id ?? '',
                    province: postulanteData?.province_id ?? '',
                    district: postulanteData?.district_id ?? '',
                    zone: "",
                });
            } else {
                navigate('/admin/rrhh');
                return;
            }

            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response?.status === 404 && error.response?.data?.error === 'Postulante no encontrado') {
                navigate('/admin/rrhh');
                return;
            }
            console.error("Error al obtener el postulante:", error);
        }
    }, [token, id, navigate]);


    const handlePerfilChange = (e) => {
        const nuevoPerfil = e.target.value;
        setForm((prev) => ({
            ...prev,
            perfil: nuevoPerfil,
            // Limpiar jefe y supervisor cuando cambia el perfil
            jefe: "",
            supervisor: "",
        }));

        // Si el perfil es Jefe, cargar supervisores
        if (nuevoPerfil === "Jefe") {
            cargarSupervisores();
        }
        // Si el perfil es Supervisor, cargar jefes
        else if (nuevoPerfil === "Supervisor") {
            cargarJefes();
        }
        // Si el perfil es Vendedor, Callcenter o Asesor, cargar ambos
        else if (["Vendedor", "Callcenter", "Asesor"].includes(nuevoPerfil)) {
            cargarJefes();
            cargarSupervisores();
        }
    };

    const buscarCliente = async () => {
        try {
            if (!form.numero || form.numero.trim() === "") {
                setErrors((prev) => ({
                    ...prev,
                    numero: "Ingrese un número válido",
                }));
                return;
            }

            const cliente = await ClienteService.buscarPorDNI({ token, numero: form.numero });

            if (cliente) {
                setForm((prev) => ({
                    ...prev,
                    nombre: cliente.nombres,
                    apellidoMaterno: cliente.apellido_materno,
                    apellidoPaterno: cliente.apellido_paterno,
                }));

                setErrors((prev) => ({
                    ...prev,
                    numero: null,
                }));
            }
        } catch (err) {
            const mensaje = err.response?.data?.error || err.message || "Error al consultar el servicio";
            setErrors((prev) => ({
                ...prev,
                numero: mensaje,
            }));
        }
    };

    useEffect(() => {
        cargarRegiones();

        cargarTipoDocumentos();

        cargarPerfiles();

        // Cargar jefes y supervisores por defecto
        cargarJefes();
        cargarSupervisores();

        fetchPostulantes();
        setErrors({});
        setSuccess("");
    }, [cargarRegiones, cargarTipoDocumentos, cargarPerfiles, cargarJefes, cargarSupervisores, fetchPostulantes]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: value ?? "",
        }));
    };


    // Funciones para manejar cambios de ubicación
    const handleDepartmentChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            department: value,
            province: "",
            district: "",
            zone: "",
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            departamento: value,
            provincia: "",
            distrito: "",
            region: "",
        }));
    };

    const handleProvinceChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            province: value,
            district: "",
            zone: "",
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            provincia: value,
            distrito: "",
            region: "",
        }));
    };

    const handleDistrictChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            district: value,
            zone: "",
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            distrito: value,
            region: "",
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const iso = date.toISOString();
        return iso.split('T')[0]; // devuelve YYYY-MM-DD
    };

    const handleZoneChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            zone: value,
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            region: value,
        }));
    };

    const validate = () => {
        let errs = {};
        const required = (field, key) => { if (!field) errs[key] = key + " es Requerido"; };

        required(form.tipoDocumento, "tipoDocumento");
        required(form.numero, "numero");
        required(form.perfil, "perfil");
        required(form.nombre, "nombre");
        required(form.email, "email");
        required(form.departamento, "department");
        required(form.provincia, "provinces");
        required(form.distrito, "districts");
        required(form.id_region, "region");

        const nombrePerfil = tipoSeleccionado?.nombre;
        if (nombrePerfil === "Supervisor") {
            required(form.jefe, "jefe");
        } else if (nombrePerfil !== "Jefe" && perfilesEquipoTrabajo.has(nombrePerfil)) {
            required(form.jefe, "jefe");
            required(form.supervisor, "supervisor");
        }
        return errs;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);

        if (Object.keys(errs).length === 0) {
            setLoading(true);
            try {
                const userData = {
                    tipoDocumento: form.tipoDocumento,
                    documento: form.numero,
                    perfil: form.perfil,
                    nombre: form.nombre,
                    apellidoPaterno: form.apellidoPaterno,
                    apellidoMaterno: form.apellidoMaterno,
                    celular: form.celular,
                    email: form.email,
                    password: form.password,
                    fechaNacimiento: form.fechaNacimiento,
                    departamento: form.departamento,
                    provincia: form.provincia,
                    distrito: form.distrito,
                    direccion: form.direccion,
                    id_region: form.id_region,
                    jefe: form.jefe,
                    supervisor: form.supervisor,
                    id_postulante: form.id_postulante,
                    usuario: form.usuario
                };

                await UsuarioService.guardarUsuario({
                    token,
                    userData
                });

                toast.success('Usuario registrado correctamente');
                navigate('/admin/rrhh');

            } catch (error) {
                console.error("Error al guardar usuario:", error);
                setErrors({
                    general: error.response?.data?.error || "Error al guardar el usuario",
                });
            } finally {
                setLoading(false);
            }
        }

    };

    // Función para obtener el nombre completo de un usuario
    const getNombreCompleto = (usuario) => {
        return `${usuario.nombre || ""} ${usuario.apellidoPaterno || ""} ${usuario.apellidoMaterno || ""
            }`.trim();
    };

    return (
        <div className="ventas-admin-content">
            {loading && <Preloader />}
            <div className="breadcrumb-container">
                <button type="button" className="breadcrumb-link" onClick={handleBackToList}>
                    ← Listado de Postulantes
                </button>
                <span className="breadcrumb-current"> / Registrar Usuario</span>
            </div>
            {success && (
                <div
                    style={{
                        color: "green",
                        textAlign: "center",
                        margin: "10px 0",
                        fontWeight: 600,
                    }}
                >
                    {success}
                </div>
            )}
            {errors.general && (
                <div
                    style={{
                        color: "red",
                        textAlign: "center",
                        margin: "10px 0",
                        fontWeight: 600,
                    }}
                >
                    {errors.general}
                </div>
            )}
            <div className="dashboard-card nuevo-producto-card">
                <span className="nueva-venta-title"> Registrar Usuario</span>
                <form onSubmit={handleSave} className="nuevo-producto-form">
                    <div className="form-section">
                        <h3 className="section-title">Datos del Usuario</h3>
                        <div className="form-grid">
                            <div>
                                <label htmlFor="tipoDocumento">Tipo documento*</label>
                                <select
                                    name="tipoDocumento"
                                    value={form.tipoDocumento}
                                    onChange={handleChange}

                                >
                                    <option value="">Seleccionar</option>
                                    {tiposDocumentos.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.description}
                                        </option>
                                    ))}
                                </select>
                                {errors.tipoDocumento && (
                                    <span className="error">{errors.tipoDocumento}</span>
                                )}
                            </div>
                            <div>
                                <label htmlFor="numero">Número*</label>
                                <div className="input-with-button">
                                    <input
                                        name="numero"
                                        value={form.numero}
                                        onChange={handleNumericChange}

                                        placeholder="Solo números"
                                    />
                                    {form.tipoDocumento === "1" && (
                                        <button
                                            type="button"
                                            className="search-btn-documento"
                                            title="Buscar cliente"
                                            onClick={buscarCliente}
                                        >
                                            <Search size={20} color="#555" />
                                        </button>
                                    )}
                                </div>
                                {errors.numero && (
                                    <span className="error">{errors.numero}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label htmlFor="perfil">Perfil*</label>
                                <select
                                    name="perfil"
                                    value={form.perfil}
                                    onChange={handlePerfilChange}

                                >
                                    <option value="">Seleccionar</option>
                                    {perfiles.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.nombre}
                                        </option>
                                    ))}
                                </select>
                                {errors.perfil && (
                                    <span className="error">{errors.perfil}</span>
                                )}
                            </div>
                            <div>
                                <label htmlFor="nombre">Nombres</label>
                                <input
                                    name="nombre"
                                    value={form.nombre}
                                    onChange={handleChange}

                                />
                                {errors.nombre && (
                                    <span className="error">{errors.nombre}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label htmlFor="apellidoPaterno">Apellido paterno</label>
                                <input
                                    name="apellidoPaterno"
                                    value={form.apellidoPaterno}
                                    onChange={handleChange}

                                />
                            </div>
                            <div>
                                <label htmlFor="apellidoMaterno">Apellido materno</label>
                                <input
                                    name="apellidoMaterno"
                                    value={form.apellidoMaterno}
                                    onChange={handleChange}

                                />
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label htmlFor="celular">Número de celular</label>
                                <input
                                    name="celular"
                                    value={form.celular}
                                    onChange={handleNumericChange}

                                    placeholder="Solo números"
                                />
                            </div>
                            <div>
                                <label htmlFor="email">Correo electrónico</label>
                                <input
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}

                                />
                                {errors.email && <span className="error">{errors.email}</span>}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label htmlFor="fechaNacimiento">Fecha de Nacimiento</label>
                                <input
                                    type="date"
                                    name="fechaNacimiento"
                                    value={form.fechaNacimiento}
                                    onChange={handleChange}

                                />
                            </div>
                            <div>
                                <label htmlFor="usuario">Código Usuario</label>
                                <input
                                    name="usuario"
                                    value={form.usuario}
                                    onChange={handleChange}

                                />
                                {errors.usuario && (
                                    <span className="error">{errors.usuario}</span>
                                )}
                            </div>
                        </div>

                    </div>
                    <div className="form-section">
                        <h3 className="section-title">Datos de Ubicación</h3>
                        <div style={{ gridColumn: "1 / -1" }}>

                            <LocationDropdowns
                                selectedDepartment={locationData.department}
                                selectedProvince={locationData.province}
                                selectedDistrict={locationData.district}
                                selectedZone={locationData.zone}
                                onDepartmentChange={handleDepartmentChange}
                                onProvinceChange={handleProvinceChange}
                                onDistrictChange={handleDistrictChange}
                                onZoneChange={handleZoneChange}
                                showZones={false}
                                className="location-dropdowns"
                                token={token}
                                errors={errors}
                            />
                        </div>

                        <div style={{ gridColumn: "1 / -1" }}>
                            <label htmlFor="direccion">Dirección</label>
                            <textarea
                                name="direccion"
                                value={form.direccion}
                                onChange={handleChange}
                                placeholder="Ingrese la dirección completa"
                                rows={8}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    fontSize: "14px",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    outline: "none",
                                    resize: "vertical",
                                    boxSizing: "border-box",
                                    backgroundColor: "white",
                                    transition: "border-color 0.3s ease",
                                }}
                                onFocus={(e) => (e.target.style.borderColor = "#3182ce")}
                                onBlur={(e) => (e.target.style.borderColor = "#ccc")}
                            />
                        </div>
                    </div>
                    <div className="form-section">
                        <h3 className="section-title">Datos del Perfil</h3>
                        <div className="form-grid">
                            <div>
                                <label htmlFor="id_region">Región</label>
                                <select
                                    name="id_region"
                                    value={form.id_region}
                                    onChange={handleChange}
                                    disabled={loadingRegiones}
                                >
                                    <option value="">Seleccionar región</option>
                                    {loadingRegiones ? (
                                        <option disabled>Cargando regiones...</option>
                                    ) : (
                                        regionesDisponibles.map((region) => (
                                            <option key={region.id} value={region.id}>
                                                {region.description}
                                            </option>
                                        ))
                                    )}
                                </select>
                                {errors.region && <span className="error">{errors.region}</span>}
                            </div>

                            {(tipoSeleccionado?.nombre === "Supervisor" ||
                                tipoSeleccionado?.nombre === "Vendedor" ||
                                tipoSeleccionado?.nombre === "Callcenter" ||
                                tipoSeleccionado?.nombre === "Asesor") && (
                                    <div>
                                        <label htmlFor="jefe">Jefe</label>
                                        <select
                                            name="jefe"
                                            value={form.jefe}
                                            onChange={handleChange}
                                            disabled={loadingJefes}
                                        >
                                            <option value="">Seleccionar jefe</option>
                                            {loadingJefes ? (
                                                <option disabled>Cargando jefes...</option>
                                            ) : (
                                                jefesDisponibles.map((jefe) => (
                                                    <option key={jefe.id} value={jefe.id}>
                                                        {getNombreCompleto(jefe)} - {jefe.region}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        {errors.jefe && <span className="error">{errors.jefe}</span>}
                                    </div>
                                )}

                            {(tipoSeleccionado?.nombre === "Vendedor" ||
                                tipoSeleccionado?.nombre === "Callcenter" ||
                                tipoSeleccionado?.nombre === "Asesor") && (
                                    <div>
                                        <label htmlFor="supervisor">Supervisor</label>
                                        <select
                                            name="supervisor"
                                            value={form.supervisor}
                                            onChange={handleChange}
                                            disabled={loadingSupervisores}
                                        >
                                            <option value="">Seleccionar supervisor</option>
                                            {loadingSupervisores ? (
                                                <option disabled>Cargando supervisores...</option>
                                            ) : (
                                                supervisoresDisponibles.map((supervisor) => (
                                                    <option key={supervisor.id} value={supervisor.id}>
                                                        {getNombreCompleto(supervisor)} - {supervisor.region}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        {errors.supervisor && <span className="error">{errors.supervisor}</span>}
                                    </div>
                                )}

                        </div>
                    </div>

                    <div className="form-actions">

                        <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? "Registrando..." : " Registrar"}
                        </button>
                    </div>

                </form>
            </div>
        </div>

    );
}

export default NuevoUsuario;
